import React from "react";
import { debounce } from "lodash";
import request from "lib/request";
import User from "User";

class BaseReport extends React.Component {
  state = {
    filters: {
      fromDate: null,
      toDate: null,
      storeId: "",
      itemIds: [],
    },
    stores: [],
    itemSearch: "",
    itemSuggestions: [],
    selectedItems: [],
    showSuggestions: false,
    downloading: false,
  };

  componentDidMount() {
    this.props.fetchData(this.getBaseEndpoint());
  }

  // Abstract methods to be implemented by subclasses
  getBaseEndpoint() {
    throw new Error("getBaseEndpoint() must be implemented");
  }

  getSearchEndpoint() {
    throw new Error("getSearchEndpoint() must be implemented");
  }

  getDownloadFilename() {
    throw new Error("getDownloadFilename() must be implemented");
  }

  getItemIdsParamName() {
    return "item_ids";
  }

  getItemsDataKey() {
    return "items";
  }

  // Shared methods
  handleFilterChange = (field, value) => {
    this.setState(
      (prev) => ({
        filters: { ...prev.filters, [field]: value },
      }),
      this.fetchFilteredData
    );
  };

  buildQueryParams = () => {
    const { filters, selectedItems } = this.state;
    const params = {
      from_date: filters.fromDate,
      to_date: filters.toDate,
      store_id: filters.storeId,
    };

    if (selectedItems.length > 0) {
      params[this.getItemIdsParamName()] = selectedItems.map((item) => item.id).join(",");
    }

    Object.keys(params).forEach(
      (k) => (params[k] === "" || params[k] == null) && delete params[k]
    );
    return params;
  };

  filteredURL = () => {
    const params = this.buildQueryParams();
    const query = new URLSearchParams(params).toString();
    const endpoint = this.getBaseEndpoint();
    return query ? `${endpoint}?${query}` : endpoint;
  };

  handleDownload = () => {
    this.setState({ downloading: true });

    const params = this.buildQueryParams();
    const query = new URLSearchParams(params).toString();
    const url = `/api/${this.getBaseEndpoint()}/download?${query}`;

    const authToken = User.getAuthToken();
    const csrfToken = document.getElementsByName('csrf-token')[0].content;

    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Authorization', authToken);
    xhr.setRequestHeader('X-CSRF-Token', csrfToken);
    xhr.setRequestHeader('X-CURRENT-PAGE', window.location.href);
    xhr.responseType = 'blob';

    xhr.onload = () => {
      this.setState({ downloading: false });
      if (xhr.status === 200) {
        const blob = xhr.response;
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = this.getDownloadFilename();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        console.error('Download failed with status:', xhr.status);
      }
    };

    xhr.onerror = () => {
      this.setState({ downloading: false });
      console.error('XHR error occurred');
    };

    xhr.send();
  };

  fetchFilteredData = () => {
    this.props.fetchData(this.filteredURL());
  };

  changePage = (page) => {
    const url = this.filteredURL();
    const joinChar = url.indexOf("?") > 0 ? "&" : "?";
    this.props.fetchData(`${url}${joinChar}page=${page}`);
  };

  searchItems = debounce((term) => {
    if (term.length < 2) {
      this.setState({ itemSuggestions: [], showSuggestions: false });
      return;
    }

    request.get(`/${this.getSearchEndpoint()}?term=${encodeURIComponent(term)}`)
      .then(({ data }) => {
        const dataKey = this.getItemsDataKey();
        const suggestions = data[dataKey].filter(
          (item) => !this.state.selectedItems.find((si) => si.id === item.id)
        );
        this.setState({ itemSuggestions: suggestions, showSuggestions: true });
      });
  }, 300);

  handleItemSearchChange = (e) => {
    const term = e.target.value;
    this.setState({ itemSearch: term });
    this.searchItems(term);
  };

  selectItem = (item) => {
    this.setState(
      (prev) => ({
        selectedItems: [...prev.selectedItems, item],
        itemSearch: "",
        itemSuggestions: [],
        showSuggestions: false,
      }),
      this.fetchFilteredData
    );
  };

  removeItem = (itemId) => {
    this.setState(
      (prev) => ({
        selectedItems: prev.selectedItems.filter((item) => item.id !== itemId),
      }),
      this.fetchFilteredData
    );
  };

  clearAllItems = () => {
    this.setState(
      {
        selectedItems: [],
        itemSearch: "",
      },
      this.fetchFilteredData
    );
  };
}

export default BaseReport;
